import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaTrash,
} from "react-icons/fa";
import swal from "sweetalert";
import { getToken } from "../utils/auth";
import AssignmentProblemsPieChart from "./AssignmentProblemsPieChart";
import "./AssignmentSubmissionsReport.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_API_BASE_URL;

function SolvedDistributionTable({ solvedCounts }) {
  return (
    <div className="solved-table">
      <h3>Student Distribution by Problems Solved</h3>
      <br />
      <table className="solved-distribution-table">
        <thead>
          <tr>
            <th>Problems Solved</th>
            <th>Number of Students</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(solvedCounts).map(([range, count]) => (
            <tr key={range}>
              <td>{range}</td>
              <td>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AssignmentSubmissionsReport() {
  const { assignmentId } = useParams();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCollege, setSelectedCollege] = useState("all");
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const res = await fetch(
          `${API}/api/assignment-submissions/assignment/${assignmentId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        const data = await res.json();
        setSubmissions(data);
      } catch (err) {
        console.error("Failed to fetch submissions", err);
        swal("Error", "Failed to load submission data.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [assignmentId]);

  /* ================= DELETE HANDLER WITH SWAL ================= */
  const handleDeleteSubmission = async (id) => {
    // Confirmation modal using SweetAlert
    const willDelete = await swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this submission!",
      icon: "warning",
      buttons: ["Cancel", "Yes, delete it!"],
      dangerMode: true,
    });

    if (!willDelete) return;

    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/deleteSubmissionById`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ id }),
      });

      const result = await res.json();

      if (res.ok) {
        // Remove deleted item from local state UI
        setSubmissions((prev) => prev.filter((sub) => sub._id !== id));
        
        // Success popup using SweetAlert
        swal("Deleted!", "The submission has been deleted successfully.", "success");
      } else {
        swal("Error", result.message || "Failed to delete submission.", "error");
      }
    } catch (err) {
      console.error("Error deleting submission:", err);
      swal("Error", "Something went wrong while deleting.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  /* ================= FILTER VALUES ================= */
  const colleges = [
    ...new Set(submissions.map((s) => s.studentDetails?.college).filter(Boolean)),
  ];

  const batches = [
    ...new Set(submissions.map((s) => s.studentDetails?.batch).filter(Boolean)),
  ];

  const years = [
    ...new Set(submissions.map((s) => s.studentDetails?.year).filter(Boolean)),
  ];

  /* ================= FILTER LOGIC ================= */
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((s) => {
      const searchLower = searchTerm.toLowerCase();

      const matchesSearch =
        s.studentDetails?.name?.toLowerCase().includes(searchLower) ||
        s.studentDetails?.email?.toLowerCase().includes(searchLower) ||
        s.studentDetails?.regNo?.toLowerCase().includes(searchLower);

      const matchesCollege =
        selectedCollege === "all" ||
        s.studentDetails?.college === selectedCollege;

      const matchesBatch =
        selectedBatch === "all" ||
        s.studentDetails?.batch === selectedBatch;

      const matchesYear =
        selectedYear === "all" ||
        s.studentDetails?.year === selectedYear;

      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "completed" && s.isCompleted) ||
        (selectedStatus === "incomplete" && !s.isCompleted);

      return (
        matchesSearch &&
        matchesCollege &&
        matchesBatch &&
        matchesYear &&
        matchesStatus
      );
    });
  }, [
    submissions,
    searchTerm,
    selectedCollege,
    selectedBatch,
    selectedYear,
    selectedStatus,
  ]);

  /* ================= SOLVED COUNTS ================= */
  const solvedCounts = useMemo(() => {
    const counts = {
      "3 Solved": 0,
      "2 Solved": 0,
      "1 Solved": 0,
      "0 Solved": 0,
    };

    filteredSubmissions.forEach((sub) => {
      const solved = sub.problemsSolved || 0;
      if (solved >= 3) counts["3 Solved"]++;
      else if (solved === 2) counts["2 Solved"]++;
      else if (solved === 1) counts["1 Solved"]++;
      else counts["0 Solved"]++;
    });

    return counts;
  }, [filteredSubmissions]);

  /* ================= CSV DOWNLOAD ================= */
  const downloadCSV = () => {
    if (!filteredSubmissions.length) {
      swal("Notice", "No data available to export.", "info");
      return;
    }

    const headers = [
      "S.No",
      "Student Name",
      "Email",
      "Reg No",
      "College",
      "Year",
      "Batch",
      "Problems Solved",
      "Status",
      "Submitted On",
    ];

    const rows = filteredSubmissions.map((s, index) => [
      index + 1,
      s.studentDetails?.name || "",
      s.studentDetails?.email || "",
      s.studentDetails?.regNo || "",
      s.studentDetails?.college || "",
      s.studentDetails?.year || "",
      s.studentDetails?.batch || "",
      s.problemsSolved ?? 0,
      s.isCompleted ? "Completed" : "Incomplete",
      s.createdAt
        ? new Date(s.createdAt).toLocaleDateString()
        : "",
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) => row.map((val) => `"${val}"`).join(","))
        .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `assignment_report_${assignmentId}.csv`;
    link.click();
    
    swal("Downloaded!", "CSV report has been exported successfully.", "success");
  };

  /* ================= EXCEL DOWNLOAD - CONSOLIDATED ================= */
  const downloadExcel = () => {
    if (!filteredSubmissions.length) {
      swal("Notice", "No data available to export.", "info");
      return;
    }

    const data = filteredSubmissions.map((s, index) => ({
      "S.No": index + 1,
      "Student Name": s.studentDetails?.name || "",
      Email: s.studentDetails?.email || "",
      "Reg No": s.studentDetails?.regNo || "",
      College: s.studentDetails?.college || "",
      Year: s.studentDetails?.year || "",
      Batch: s.studentDetails?.batch || "",
      "Problems Solved": s.problemsSolved ?? 0,
      Status: s.isCompleted ? "Completed" : "Incomplete",
      "Submitted On": s.createdAt
        ? new Date(s.createdAt).toLocaleDateString()
        : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    XLSX.utils.book_append_sheet(wb, ws, "All Students");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buffer]),
      `assignment_all_${assignmentId}.xlsx`
    );

    swal("Downloaded!", "Excel report has been exported successfully.", "success");
  };

  /* ================= EXCEL DOWNLOAD - BATCH WISE ================= */
  const downloadBatchExcel = () => {
    if (!filteredSubmissions.length) {
      swal("Notice", "No data available to export.", "info");
      return;
    }

    const batchMap = {};

    filteredSubmissions.forEach((s) => {
      const batch = s.studentDetails?.batch || "Unknown";

      if (!batchMap[batch]) {
        batchMap[batch] = [];
      }

      batchMap[batch].push({
        "Student Name": s.studentDetails?.name || "",
        Email: s.studentDetails?.email || "",
        "Reg No": s.studentDetails?.regNo || "",
        College: s.studentDetails?.college || "",
        Year: s.studentDetails?.year || "",
        Batch: batch,
        "Problems Solved": s.problemsSolved ?? 0,
        Status: s.isCompleted ? "Completed" : "Incomplete",
        "Submitted On": s.createdAt
          ? new Date(s.createdAt).toLocaleDateString()
          : "",
      });
    });

    const wb = XLSX.utils.book_new();

    Object.keys(batchMap).forEach((batch) => {
      const ws = XLSX.utils.json_to_sheet(batchMap[batch]);
      XLSX.utils.book_append_sheet(wb, ws, batch);
    });

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buffer]),
      `assignment_batches_${assignmentId}.xlsx`
    );

    swal("Downloaded!", "Batch-wise Excel report exported successfully.", "success");
  };

  /* ================= RESET FILTERS ================= */
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCollege("all");
    setSelectedBatch("all");
    setSelectedYear("all");
    setSelectedStatus("all");
  };

  const isFiltered =
    searchTerm ||
    selectedCollege !== "all" ||
    selectedBatch !== "all" ||
    selectedYear !== "all" ||
    selectedStatus !== "all";

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="page-loader">
        <div className="spinner"></div>
        <p>Loading Submissions...</p>
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="assignment-submissions-report">
      {/* HEADER */}
      <div className="report-header">
        <div>
          <h2>Assignment Submissions</h2>
          <p className="subtitle">
            Dynamic distribution from 0 → Highest Problems Solved
          </p>
        </div>

        <div className="header-right">
          <div className="search-box-compact">
            <FaSearch />
            <input
              type="text"
              placeholder="Search student..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")}>
                <FaTimes />
              </button>
            )}
          </div>

          <button className="download-btn" onClick={downloadCSV}>
            <FaDownload /> Download CSV
          </button>

          <button className="download-btn" onClick={downloadExcel}>
            <FaDownload /> Excel
          </button>

          <button className="download-btn" onClick={downloadBatchExcel}>
            <FaDownload /> Batch Wise
          </button>

          <span className="count-badge">
            Showing: {filteredSubmissions.length} / {submissions.length}
          </span>
        </div>
      </div>

      {/* FILTERS */}
      <div className="filters-container">
        <div className="filters-header">
          <FaFilter /> Filters
          {isFiltered && (
            <button onClick={resetFilters}>Reset All</button>
          )}
        </div>

        <div className="filters-grid">
          <select value={selectedCollege} onChange={(e) => setSelectedCollege(e.target.value)}>
            <option value="all">All Colleges</option>
            {colleges.map((college) => (
              <option key={college}>{college}</option>
            ))}
          </select>

          <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
            <option value="all">All Batches</option>
            {batches.map((batch) => (
              <option key={batch}>{batch}</option>
            ))}
          </select>

          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="all">All Years</option>
            {years.map((year) => (
              <option key={year}>{year}</option>
            ))}
          </select>

          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="incomplete">Incomplete</option>
          </select>
        </div>
      </div>

      {/* ANALYTICS */}
      <div className="analytics-section">
        <h3>Problems Solved Distribution</h3>
        <div className="chart-container">
          <AssignmentProblemsPieChart submissions={filteredSubmissions} />
        </div>
      </div>

      <SolvedDistributionTable solvedCounts={solvedCounts} />

      {/* TABLE */}
      <div className="table-wrapper">
        <table className="report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Student</th>
              <th>Reg No</th>
              <th>College</th>
              <th>Year</th>
              <th>Batch</th>
              <th>Solved</th>
              <th>Status</th>
              <th>Submitted On</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredSubmissions.length > 0 ? (
              filteredSubmissions.map((s, index) => (
                <tr key={s._id}>
                  <td>{index + 1}</td>
                  <td>
                    <FaUser /> {s.studentDetails?.name}
                    <br />
                    <FaEnvelope /> {s.studentDetails?.email}
                  </td>
                  <td>{s.studentDetails?.regNo}</td>
                  <td>{s.studentDetails?.college}</td>
                  <td>{s.studentDetails?.year}</td>
                  <td>{s.studentDetails?.batch}</td>
                  <td>{s.problemsSolved}</td>
                  <td>
                    {s.isCompleted ? (
                      <span className="status completed">
                        <FaCheckCircle /> Completed
                      </span>
                    ) : (
                      <span className="status pending">
                        <FaTimesCircle /> Incomplete
                      </span>
                    )}
                  </td>
                  <td>
                    <FaCalendarAlt />{" "}
                    {new Date(s.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteSubmission(s._id)}
                      disabled={deletingId === s._id}
                      style={{
                        backgroundColor: "#e63946",
                        color: "#fff",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      <FaTrash /> {deletingId === s._id ? "..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-data">
                  No submissions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}